import type { InvoiceRecord } from '@navio/nsdk';
import { revalidateLogic } from '@tanstack/react-form';
import { invalidateInvoiceQueries, noddiAsync, URLKeys } from 'noddi-async';
import { NoddiSheet, useAppForm } from 'noddi-ui-web';
import { NoddiDateFormats, noddiFormatDate, parseDate } from 'noddi-util';
import { z } from 'zod';

const FORM_ID = 'edit-invoice-period-form';

const validationSchema = z
  .object({
    fromDate: z.date({ message: 'Required' }),
    toDate: z.date({ message: 'Required' }),
    dueDate: z.date().optional()
  })
  .refine((value) => value.fromDate <= value.toDate, {
    message: 'To date must be on or after from date',
    path: ['toDate']
  });

type FormValues = z.input<typeof validationSchema>;

type Props = {
  onClose: () => void;
  invoice: InvoiceRecord;
  invoiceId: number;
};

const toDate = (value?: string | null): Date | undefined =>
  value ? parseDate(value, NoddiDateFormats.API_DATE) : undefined;

const toApiDate = (value: Date) => noddiFormatDate(value, NoddiDateFormats.API_DATE);

export const EditInvoicePeriodSheet = ({ onClose, invoice, invoiceId }: Props) => {
  const { mutateAsync: patchInvoice } = noddiAsync.usePatch({
    type: URLKeys.patchInvoice,
    queryConfig: {
      onSuccess: async () => {
        await invalidateInvoiceQueries({ invoiceId });
        onClose();
      }
    }
  });

  const defaultValues: FormValues = {
    fromDate: toDate(invoice.fromDate) ?? new Date(),
    toDate: toDate(invoice.toDate) ?? new Date(),
    dueDate: toDate(invoice.dueDate)
  };

  const form = useAppForm({
    defaultValues,
    validationLogic: revalidateLogic(),
    validators: { onDynamic: validationSchema },
    onSubmit: async ({ value }) => {
      await patchInvoice({
        invoiceId,
        fromDate: toApiDate(value.fromDate),
        toDate: toApiDate(value.toDate),
        dueDate: value.dueDate ? toApiDate(value.dueDate) : null
      });
    }
  });

  return (
    <form.AppForm>
      <NoddiSheet
        open
        onOpenChange={(next) => !next && onClose()}
        title='Edit billing period'
        footer={<form.SubscribeButton formId={FORM_ID} label='Save' className='w-full' />}
      >
        <form
          id={FORM_ID}
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <div className='flex flex-col gap-5'>
            <form.AppField name='fromDate'>{(field) => <field.DatePicker label='From date' />}</form.AppField>
            <form.AppField name='toDate'>{(field) => <field.DatePicker label='To date' />}</form.AppField>
            <form.AppField name='dueDate'>
              {(field) => <field.DatePicker label='Due date' showClearButton />}
            </form.AppField>
          </div>
        </form>
      </NoddiSheet>
    </form.AppForm>
  );
};
